import { AnimationPicker } from '@/components/admin/experiences/page-editor/animation-picker';
import { blockRowLabel } from '@/components/admin/experiences/page-editor/block-stack';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { replaceBlock, replaceTemps } from '@/lib/page-editor';
import type { AnimationSpeed, ExperiencePageDraft } from '@matter_extractor/shared';
import { EXPERIENCE_TEMPS_DELAY_MAX } from '@matter_extractor/shared';

/**
 * « Régler les temps et animations » : la pile devient des PISTES (refonte
 * modulaire §9.3; planche `ModeAnimation`; issue #58).
 *
 * « Allumée, chaque temps devient une piste, sa rapidité et son délai sous la
 * main, et chaque bloc montre ses huit animations, la sienne en bleu nuit. »
 * It is « l'autre chemin vers les mêmes réglages que l'inspecteur », so it
 * writes through the SAME two functions - `replaceTemps` and `replaceBlock` -
 * and holds no rule of its own: a second spelling of « change the speed of a
 * temps » would be two rules to keep in agreement for one control.
 *
 * The first temps shows its delay as a fact and not as a control (H27: « son
 * délai est 0 et ne se règle pas »), and a temps that waits for a click says so
 * where the delay would be - the number would be a lie there.
 */

const SPEEDS: readonly { value: AnimationSpeed; label: string }[] = [
  { value: 'fast', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lent' },
];

export interface TempsTracksProps {
  readonly page: ExperiencePageDraft;
  readonly readOnly: boolean;
  readonly selectedBlockId: string | null;
  readonly onSelectBlock: (blockId: string) => void;
  readonly onPage: (next: ExperiencePageDraft) => void;
  /** « Au clic, l'aperçu rejoue l'animation » (§9.3) - the caller replays. */
  readonly onReplay: () => void;
}

export function TempsTracks({
  page,
  readOnly,
  selectedBlockId,
  onSelectBlock,
  onPage,
  onReplay,
}: TempsTracksProps) {
  return (
    <div data-testid={PAGE_EDITOR_IDS.tracks} className="space-y-2">
      {page.temps.map((temps, tempsIndex) => (
        <section
          key={temps.id}
          data-testid={PAGE_EDITOR_IDS.track}
          data-temps-id={temps.id}
          className="space-y-2 rounded-md border-2 border-gray-200 bg-white p-2"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-pill bg-bleu-nuit font-semibold text-[10.5px] text-white tabular-nums">
              {tempsIndex + 1}
            </span>
            <span className="font-semibold text-bleu-nuit text-xs">Temps {tempsIndex + 1}</span>
            <Segmented
              label={`Rapidité du temps ${tempsIndex + 1}`}
              size="sm"
              value={temps.speed}
              options={SPEEDS}
              readOnly={readOnly}
              onChange={(speed) => onPage(replaceTemps(page, { ...temps, speed }))}
            />
            {tempsIndex === 0 ? (
              <span className="text-gray-600 text-xs">part avec la page</span>
            ) : temps.start.kind === 'click' ? (
              <span className="text-gray-600 text-xs">au clic d’un bouton</span>
            ) : (
              <label className="flex items-center gap-1.5 text-gray-600 text-xs">
                délai
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={EXPERIENCE_TEMPS_DELAY_MAX}
                  value={temps.start.seconds}
                  readOnly={readOnly}
                  onChange={(event) => {
                    const seconds = Number(event.currentTarget.value);
                    if (Number.isNaN(seconds)) return;
                    onPage(
                      replaceTemps(page, {
                        ...temps,
                        start: {
                          kind: 'delay',
                          seconds: Math.min(Math.max(seconds, 0), EXPERIENCE_TEMPS_DELAY_MAX),
                        },
                      }),
                    );
                  }}
                  className="h-7 w-16 rounded-md border-2 border-gray-300 bg-white px-2 text-sm read-only:bg-gray-50 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                s
              </label>
            )}
          </div>

          <ul className="space-y-1.5">
            {temps.blocks.map((block) => (
              <li
                key={block.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md px-1 py-1"
              >
                <button
                  type="button"
                  aria-pressed={block.id === selectedBlockId}
                  onClick={() => onSelectBlock(block.id)}
                  className={`min-w-0 max-w-48 cursor-pointer truncate rounded-sm px-1 text-left text-sm hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    block.id === selectedBlockId ? 'font-semibold text-bleu-nuit' : 'text-gray-800'
                  }`}
                >
                  {blockRowLabel(block)}
                </button>
                {block.type === 'sound' ? (
                  <span className="text-gray-500 text-xs">pas d’animation d’entrée</span>
                ) : (
                  <AnimationPicker
                    label={`Animation de ${blockRowLabel(block)}`}
                    value={block.animation}
                    readOnly={readOnly}
                    onChange={(animation) => {
                      onPage(replaceBlock(page, { ...block, animation }));
                      onReplay();
                    }}
                  />
                )}
              </li>
            ))}
            {temps.blocks.length === 0 ? (
              <li className="px-1 text-gray-600 text-xs">
                Aucun bloc : glisse-en un ici, ou choisis ce temps dans « Apparaît au temps ».
              </li>
            ) : null}
          </ul>
        </section>
      ))}
    </div>
  );
}
